import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash.debounce'
import dayjs from 'dayjs'
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputRef,
  Select,
  Spin,
  Switch,
  Tooltip,
  Typography,
  message
} from 'antd'
import { MainTitle } from 'components/ui/MainTitle/MainTitle'
import { TVariantMode } from 'models/app'
import { IParsedCategory } from 'models/product'
import { productApi } from 'api/product'
import { sellerApi } from 'api/seller'
import { ISeller } from 'models/seller'
import classNames from 'classnames'
import { MenuInfo } from 'rc-menu/lib/interface'
import { IHelperProductsResponse } from 'models/promoCodes'
import {
  EDiscountStatus,
  EDiscountTargetType,
  ICreateDiscount,
  IDiscount,
  IDiscountProductsCount
} from 'models/discounts'
import { discountsApi } from 'api/discounts'
import { showServerError } from 'shared/utils/showServerError'
import {
  HIDE_INACTIVE_SELLERS_LABEL,
  isActiveSellerWithProducts
} from 'shared/utils/sellerFilters'
import { normalizeDigits } from 'shared/utils/normalizeDigits'
import { highlightText } from 'shared/utils/highlightSearchText'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import Table, { ColumnsType } from 'antd/es/table'
import { IMAGES_URL } from 'shared/constants/urls'
import noProductImage from 'assets/images/no-image-product.png'
import {
  QuestionCircleOutlined,
  SaveOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { appSelectors } from 'store/app'
import { ERoles } from 'models/auth'
import { showMessage } from 'shared/utils/showMessage'
import { useNavigate } from 'react-router-dom'
import { adminRoutes } from 'shared/constants/routes'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { MobileFilterTitle } from 'components/ui/MobileFilterTitle/MobileFilterTitle'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import {
  discountsStatuses,
  getDiscountDisplayStatus,
  getDiscountStatusLabel,
  getDiscountsStatusItems
} from 'shared/constants/discounts'
import styles from './DiscountsForm.module.scss'

interface IDiscountsFormProps {
  discountId?: string
  mode: TVariantMode
  // создание из карточки продавца у супер-админа: продавец уже выбран
  sellerId?: string
}

interface IFormValues {
  isGlobalDiscount: boolean
  name: string
  dates: [Date, Date]
  discount: string | number
  sellerId?: string // только у супер-админа
}

export const DiscountsForm: React.FC<IDiscountsFormProps> = ({
  mode,
  discountId,
  sellerId
}) => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const role = useAppSelector(appSelectors.selectRole)
  const isSuperAdmin = useMemo(() => role === ERoles.ADMIN, [role])

  const userSellerId = useAppSelector(appSelectors.selectSellerId)

  const [form] = Form.useForm<IFormValues>()

  // скидка принадлежит одному продавцу: админ выбирает его, товары фильтруются по нему
  const selectedSellerId: string | undefined = Form.useWatch('sellerId', {
    form,
    preserve: true
  })

  const [sellers, setSellers] = useState<ISeller[]>([])
  const [loadingSellers, setLoadingSellers] = useState(isSuperAdmin)
  const [hideInactiveSellers, setHideInactiveSellers] = useState(true)

  const visibleSellers = useMemo(() => {
    if (!hideInactiveSellers) return sellers
    // выбранного не прячем: иначе при редактировании скидки вместо названия
    // магазина в поле останется голый id
    return sellers.filter(
      (seller) =>
        isActiveSellerWithProducts(seller) || seller._id === selectedSellerId
    )
  }, [sellers, hideInactiveSellers, selectedSellerId])

  const [categories, setCategories] = useState<IParsedCategory[]>([])
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')
  const [selectedSubcategoriesIds, setSelectedSubcategoriesIds] = useState<
    string[]
  >([])
  const [subcategoriesSearch, setSubcategoriesSearch] = useState<string>('')

  const [products, setProducts] = useState<IHelperProductsResponse['items']>([])
  const [loadingProducts, setLoadingProducts] = useState(true)

  const [discountLoaded, setDiscountLoaded] = useState(mode === 'create')

  const [currentPage, setCurrentPage] = useState(1)
  const [total, setTotal] = useState(0)
  const pageSize = 10

  const [productNameSearch, setProductNameSearch] = useState('')
  const productNameSearchRef = useRef<InputRef>(null)

  const [selectedProductsIds, setSelectedProductsIds] = useState<string[]>([])

  const [discountStatus, setDiscountStatus] = useState<EDiscountStatus | null>(
    null
  )
  const [discountStartDate, setDiscountStartDate] = useState<string>()

  const [productsPreview, setProductsPreview] =
    useState<IDiscountProductsCount | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const previewRequestRef = useRef(0)
  const categoryOpenedRef = useRef(false)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSelectingAll, setIsSelectingAll] = useState(false)

  const getProducts = async (
    skip: number,
    data?: {
      productName?: string
      sellerId?: string
    }
  ) => {
    try {
      setLoadingProducts(true)
      const response = await discountsApi.getProducts({
        skip: String(skip),
        limit: String(pageSize),
        search: data?.productName ?? productNameSearch ?? undefined,
        sort_seller_id: isSuperAdmin
          ? data?.sellerId ?? selectedSellerId
          : userSellerId ?? undefined
      })
      setTotal(response.data.count)
      setProducts(response.data.items)
    } catch (e) {
      showServerError(e)
    } finally {
      setLoadingProducts(false)
    }
  }

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const { data } = await sellerApi.getSearchSellers(true)
        if (Array.isArray(data)) {
          setSellers(data)
        }
      } catch {
        message.error('Не удалось загрузить список продавцов')
      } finally {
        setLoadingSellers(false)
      }
    }
    if (isSuperAdmin) {
      fetchSellers()
    }
    const fetchCategories = async () => {
      try {
        const { data } = await productApi.getCategories({ parsed: 'true' })
        if (Array.isArray(data)) {
          setCategories(data as IParsedCategory[])
        }
        if (data && data[0]) {
          setSelectedCategoryId((data[0] as IParsedCategory).value)
        }
      } catch {
        message.error('Не удалось загрузить список категорий')
      } finally {
        setLoadingCategories(false)
      }
    }

    fetchCategories()
    // useWatch отдаёт начальное значение формы только после первого рендера
    getProducts(0, { sellerId })
  }, [])

  // запланированная и истёкшая — это ACTIVE по датам, бэкенд присылает признаки
  const applyDiscountStatus = (data: IDiscount) => {
    setDiscountStatus(getDiscountDisplayStatus(data))
    setDiscountStartDate(data.start_date)
  }

  const refreshDiscountStatus = async () => {
    if (!discountId) return
    try {
      const { data } = await discountsApi.getDiscount(discountId)
      applyDiscountStatus(data)
    } catch {
      // шапка обновится при следующем открытии скидки
    }
  }

  useEffect(() => {
    if (mode === 'edit' && discountId) {
      const fetchDiscount = async () => {
        try {
          const { data } = await discountsApi.getDiscount(discountId)
          form.setFields([
            {
              name: 'isGlobalDiscount',
              value: data.target_type === EDiscountTargetType.GLOBAL
            },
            { name: 'name', value: data.name },
            {
              name: 'dates',
              value: [dayjs(data.start_date), dayjs(data.end_date)]
            },
            { name: 'discount', value: data.discount_value },
            {
              name: 'sellerId',
              // seller_id приходит с populate — берём _id
              value:
                typeof data.seller_id === 'string'
                  ? data.seller_id
                  : data.seller_id?._id
            }
          ])
          if (data.target_products) {
            setSelectedProductsIds(data.target_products)
          }
          if (data.target_subcategories) {
            setSelectedSubcategoriesIds(data.target_subcategories)
          }
          applyDiscountStatus(data)
        } catch (error) {
          showServerError(error)
        } finally {
          setDiscountLoaded(true)
        }
      }
      fetchDiscount()
    }
  }, [discountId])

  // при правке открываем категорию с выбранными подкатегориями, а не первую в списке
  useEffect(() => {
    if (mode !== 'edit' || categoryOpenedRef.current) return
    if (!discountLoaded || !categories.length) return
    categoryOpenedRef.current = true
    const categoryWithSelected = categories.find((category) =>
      category.children.some((subcategory) =>
        selectedSubcategoriesIds.includes(subcategory.value)
      )
    )
    if (categoryWithSelected) {
      setSelectedCategoryId(categoryWithSelected.value)
    }
  }, [mode, discountLoaded, categories, selectedSubcategoriesIds])

  const onRowSelection = (selectedItemsId: React.Key[]) => {
    setSelectedProductsIds(selectedItemsId as string[])
    // выбор конкретных товаров исключает выбор по категориям — снимаем подкатегории
    if (selectedItemsId.length) {
      setSelectedSubcategoriesIds([])
    }
  }

  const onPageChange = (current: number) => {
    getProducts((current - 1) * pageSize)
    setCurrentPage(current)
  }

  const onSaveProductName = (nameSearch: string) => {
    getProducts(0, { productName: nameSearch })
    setCurrentPage(1)
  }

  const debouncedGetProductsByName = useCallback(
    debounce(onSaveProductName, DEBOUNCE_DELAY),
    []
  )

  // выбрать все товары по текущему фильтру (поиску/продавцу), а не только текущую страницу
  const selectAllFoundProducts = async () => {
    try {
      setIsSelectingAll(true)
      const response = await discountsApi.getProducts({
        skip: '0',
        limit: String(total),
        search: productNameSearch || undefined,
        sort_seller_id: isSuperAdmin
          ? selectedSellerId
          : userSellerId ?? undefined
      })
      onRowSelection(response.data.items.map((item) => item._id))
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSelectingAll(false)
    }
  }

  // смена продавца меняет пул товаров — сбрасываем выбор и перезагружаем таблицу
  const onSellerChange = (sellerId: string | undefined) => {
    setSelectedProductsIds([])
    getProducts(0, { sellerId })
    setCurrentPage(1)
  }

  const onFinish = async (values: IFormValues) => {
    if (isSuperAdmin && !values.sellerId) {
      return showMessage(
        'error',
        'Выберите продавца, которому принадлежит скидка',
        ''
      )
    }
    if (
      !values.isGlobalDiscount &&
      !selectedProductsIds.length &&
      !selectedSubcategoriesIds.length
    ) {
      return showMessage(
        'error',
        'Выберите подкатегории или товары, чтобы создать скидку, либо включите «На все товары»',
        ''
      )
    }

    const discountValue = String(values.discount).replace(/\D/g, '')

    const data: ICreateDiscount = {
      name: values.name,
      start_date: dayjs(values.dates[0]).startOf('day').format(),
      end_date: dayjs(values.dates[1]).endOf('day').format(),
      discount_value: Number(discountValue),
      target_type: values.isGlobalDiscount
        ? EDiscountTargetType.GLOBAL
        : EDiscountTargetType.SPECIFIC,
      target_subcategories:
        selectedProductsIds.length > 0 || values.isGlobalDiscount
          ? []
          : selectedSubcategoriesIds,
      target_products: values.isGlobalDiscount ? [] : selectedProductsIds,
      seller_id: isSuperAdmin ? values.sellerId : undefined
    }
    // защита от двойного клика: второй сабмит успевает проскочить до ререндера кнопки
    if (isSubmitting) return

    try {
      setIsSubmitting(true)
      if (mode === 'edit' && discountId) {
        await discountsApi.updateDiscount(discountId, data)
        message.success('Скидка успешно обновлена')
        refreshDiscountStatus()
      } else {
        const { data: created } = await discountsApi.createDiscount(data)
        message.success('Скидка успешно создана')
        // уходим на страницу редактирования, иначе повторный «Сохранить»
        // в режиме create плодит дубли скидки
        navigate(adminRoutes.DISCOUNTS_EDIT.replace(':id', created._id), {
          replace: true
        })
      }
    } catch (error) {
      showServerError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const changeDiscountStatus = async (e: MenuInfo) => {
    try {
      message.loading('Обновление статуса')
      await discountsApi.changeDiscountStatus(discountId as string, {
        status: e.key as EDiscountStatus
      })
      setDiscountStatus(e.key as EDiscountStatus)
      refreshDiscountStatus()
      message.destroy()
      message.success('Статус успешно сменен')
    } catch (e) {
      message.destroy()
      showServerError(e)
    }
  }

  const isGlobalDiscount: boolean | undefined = Form.useWatch(
    'isGlobalDiscount',
    {
      form,
      preserve: true
    }
  )

  // скидка идёт либо на подкатегории целиком, либо на отдельные товары:
  // пока выбрано одно, блок другого выключен. Если в скидке есть и то и другое,
  // главнее товары — так же решает сохранение, и товары остаются доступны для сброса
  const isSubcategoriesLocked = selectedProductsIds.length > 0
  const isProductsLocked =
    selectedSubcategoriesIds.length > 0 && !isSubcategoriesLocked

  // Сколько товаров получит скидку с текущим выбором — считает бэкенд теми же
  // правилами, что и при применении. Ответ устаревшего запроса отбрасывается
  useEffect(() => {
    if (!discountLoaded) return
    const requestId = ++previewRequestRef.current
    const previewSellerId = isSuperAdmin ? selectedSellerId : undefined

    if (isSuperAdmin && !previewSellerId) {
      setProductsPreview(null)
      setLoadingPreview(false)
      return
    }
    if (
      !isGlobalDiscount &&
      !selectedProductsIds.length &&
      !selectedSubcategoriesIds.length
    ) {
      setProductsPreview({ total: 0, on_sale: 0 })
      setLoadingPreview(false)
      return
    }

    setLoadingPreview(true)
    const timer = setTimeout(async () => {
      try {
        const { data } = await discountsApi.previewProducts({
          target_type: isGlobalDiscount
            ? EDiscountTargetType.GLOBAL
            : EDiscountTargetType.SPECIFIC,
          target_subcategories:
            selectedProductsIds.length > 0 || isGlobalDiscount
              ? []
              : selectedSubcategoriesIds,
          target_products: isGlobalDiscount ? [] : selectedProductsIds,
          seller_id: previewSellerId,
          discount_id: mode === 'edit' ? discountId : undefined
        })
        if (requestId === previewRequestRef.current) setProductsPreview(data)
      } catch {
        if (requestId === previewRequestRef.current) setProductsPreview(null)
      } finally {
        if (requestId === previewRequestRef.current) setLoadingPreview(false)
      }
    }, DEBOUNCE_DELAY)
    return () => clearTimeout(timer)
  }, [
    discountLoaded,
    isGlobalDiscount,
    selectedProductsIds,
    selectedSubcategoriesIds,
    selectedSellerId,
    isSuperAdmin,
    mode,
    discountId
  ])

  const columns: ColumnsType<IHelperProductsResponse['items'][number]> =
    useMemo(() => {
      return [
        {
          title: 'Фото',
          dataIndex: 'photo',
          width: 76,
          render: (_, { photo }) => {
            return photo && !!photo.length ? (
              <div
                style={{
                  width: 62,
                  height: 34,
                  backgroundImage: `url(${IMAGES_URL}${photo[0]})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            ) : (
              <img
                src={noProductImage}
                alt='Изображение товара'
                width={62}
                height={34}
                style={{ objectFit: 'cover' }}
              />
            )
          }
        },
        {
          title: 'Наименование товара',
          dataIndex: 'name',
          render: (_, { name }) => highlightText(name ?? '', productNameSearch),
          filterDropdown: ({ close }) => {
            return (
              <div
                className='filterWrapper'
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Input
                  ref={productNameSearchRef}
                  value={productNameSearch}
                  onChange={(e) => setProductNameSearch(e.target.value)}
                  onPressEnter={() => {
                    onSaveProductName(productNameSearch)
                    close()
                  }}
                  placeholder='Наименование товара'
                  className='filterInput'
                />
                <div className='filterFooterButtons'>
                  <Button
                    type='primary'
                    onClick={() => {
                      onSaveProductName(productNameSearch)
                      close()
                    }}
                    icon={<SearchOutlined />}
                    size='small'
                    className='filterButton'
                  >
                    Найти
                  </Button>
                  <Button
                    onClick={() => {
                      setProductNameSearch('')
                      onSaveProductName('')
                      close()
                    }}
                    size='small'
                    className='filterButton'
                  >
                    Очистить
                  </Button>
                  <Button
                    type='link'
                    size='small'
                    onClick={() => {
                      close()
                    }}
                  >
                    Закрыть
                  </Button>
                </div>
              </div>
            )
          },
          onFilterDropdownOpenChange: (visible) => {
            if (visible) {
              setTimeout(() => productNameSearchRef.current?.select(), 100)
            }
          },
          filterIcon: () => (
            <SearchOutlined
              style={{
                color: productNameSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
              }}
            />
          )
        },
        {
          title: 'Цена',
          dataIndex: 'price',
          render: (_, { price }) => {
            return (
              <Typography.Text
                onClick={(event) => event.stopPropagation()}
                style={{ opacity: price ? 1 : 0.5 }}
              >
                {price ? getNormalizedPrice(price) : 'Не задана'}
              </Typography.Text>
            )
          }
        }
      ]
    }, [products, productNameSearch])

  // Рендеры мобильных колонок берём из десктопных, чтобы правки не расходились
  const mobileColumns: ColumnsType<IHelperProductsResponse['items'][number]> =
    useMemo(() => {
      const [photoColumn, nameColumn, priceColumn] = columns

      return [
        {
          title: (
            <MobileFilterTitle
              title='Товар'
              columns={[nameColumn]}
              isActive={Boolean(productNameSearch)}
            />
          ),
          dataIndex: 'name',
          width: '62%',
          render: (value, record, index) => (
            <div className={styles.mobileProductCell}>
              <div className={styles.mobilePhoto}>
                {photoColumn?.render?.(value, record, index) as React.ReactNode}
              </div>
              <div className={styles.mobileProductInfo}>
                {nameColumn?.render?.(value, record, index) as React.ReactNode}
              </div>
            </div>
          )
        },
        {
          ...priceColumn,
          width: '38%'
        }
      ]
    }, [columns, productNameSearch])

  const isLoading = loadingCategories || loadingSellers || !discountLoaded

  if (isLoading) {
    return (
      <div
        style={{
          height: '60vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Spin size='large' />
      </div>
    )
  }

  return (
    <div style={{ paddingBottom: 80 }}>
      <div className={styles.header}>
        <MainTitle
          text={
            mode === 'create'
              ? 'Создание скидки'
              : `Скидка ${form.getFieldValue('name')}`
          }
          style={{ marginBottom: 0 }}
          isGoBack
        />
        {discountStatus && (
          <StatusDropdown
            items={getDiscountsStatusItems(discountStatus)}
            label={getDiscountStatusLabel(discountStatus, discountStartDate)}
            color={discountsStatuses[discountStatus].color}
            onClick={(e) => changeDiscountStatus(e)}
          />
        )}
      </div>
      <Form
        form={form}
        name='discount'
        size='large'
        initialValues={{ sellerId }}
        onFinish={onFinish}
      >
        <div className={styles.formHeader}>
          <Typography.Title level={4} className={styles.title}>
            Основные настройки
          </Typography.Title>
        </div>
        <div className={styles.row}>
          <Form.Item
            label='Название'
            name='name'
            rules={[{ required: true, message: 'Введите название скидки' }]}
            style={{ marginBottom: 0, flexGrow: 1 }}
          >
            <Input placeholder='Название скидки' />
          </Form.Item>
          <Form.Item
            label='Срок действия'
            name='dates'
            rules={[{ required: true, message: 'Выберите период действия' }]}
            style={{ marginBottom: 0, flexGrow: 1 }}
          >
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              format='DD.MM.YYYY'
              placeholder={['Начало', 'Конец']}
            />
          </Form.Item>
        </div>

        <div className={styles.row}>
          <Form.Item
            label='Скидка'
            name='discount'
            normalize={normalizeDigits(2)}
            rules={[
              { required: true, message: 'Введите процент скидки' },
              {
                validator: (_, value) => {
                  if (value === undefined || value === '') {
                    return Promise.resolve()
                  }
                  const percent = Number(value)
                  return percent >= 1 && percent <= 99
                    ? Promise.resolve()
                    : Promise.reject(
                      new Error('Процент скидки — целое число от 1 до 99')
                    )
                }
              }
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input placeholder='Процент скидки' suffix='%' />
          </Form.Item>
          {isSuperAdmin && (
            <Form.Item
              label={
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 12
                  }}
                >
                  Продавец
                  {/* клик по чекбоксу внутри label иначе раскрывает селект */}
                  <span onClick={(e) => e.preventDefault()}>
                    <Checkbox
                      checked={hideInactiveSellers}
                      onChange={(e) => setHideInactiveSellers(e.target.checked)}
                    >
                      <Typography.Text type='secondary' style={{ fontSize: 12 }}>
                        {HIDE_INACTIVE_SELLERS_LABEL}
                      </Typography.Text>
                    </Checkbox>
                  </span>
                </span>
              }
              name='sellerId'
              rules={[{ required: true, message: 'Выберите продавца' }]}
              style={{ marginBottom: 0, flexGrow: 1, minWidth: 260 }}
            >
              <Select
                placeholder='Выберите продавца'
                loading={loadingSellers}
                // смена продавца у существующей скидки не поддерживается бэкендом
                disabled={mode === 'edit' || Boolean(sellerId)}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={visibleSellers.map((seller) => ({
                  value: seller._id,
                  label: seller.shop_details?.shop_name || seller._id
                }))}
                onChange={(value) => onSellerChange(value)}
              />
            </Form.Item>
          )}
        </div>

        {/* заголовок области действия со свитчем виден всегда — иначе «На все товары» не выключить */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            margin: '32px 0'
          }}
        >
          <Typography.Title level={4} style={{ margin: 0 }}>
            Область действия:
          </Typography.Title>
          <Form.Item
            name='isGlobalDiscount'
            valuePropName='checked'
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
          <Typography.Text>На все товары</Typography.Text>
          {(productsPreview || loadingPreview) && (
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {productsPreview ? (
                <Typography.Text
                  type='secondary'
                  style={{ opacity: loadingPreview ? 0.5 : 1 }}
                >
                  Под скидку попадёт товаров: {productsPreview.total}, в
                  продаже: {productsPreview.on_sale}
                </Typography.Text>
              ) : (
                <Spin size='small' />
              )}
              <Tooltip title='Сколько опубликованных товаров получит скидку с этими настройками. «В продаже» — те из них, что есть на складе. Черновики, архив и снятые с продажи скидку не получают, товары с другой действующей скидкой тоже. Набор товаров фиксируется, когда скидка начинает действовать или сохраняется после правки: товары, опубликованные позже, её не получат.'>
                <QuestionCircleOutlined
                  style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }}
                />
              </Tooltip>
            </span>
          )}
          {/* при неактивном блоке категорий сохранение доступно из этого ряда, кнопка у правого края */}
          {isGlobalDiscount && (
            <Form.Item style={{ marginBottom: 0, marginLeft: 'auto' }}>
              <Button
                type='primary'
                htmlType='submit'
                icon={<SaveOutlined />}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Сохранить
              </Button>
            </Form.Item>
          )}
        </div>
        {/* слева поиск товаров + стек «Категории»/«Подкатегории», справа таблица товаров */}
        <div className={styles.columnsWrapper}>
          <div className={styles.categoriesStack}>
            <div
              className={classNames(styles.stackContent, {
                [styles.scopeDisabled]: isGlobalDiscount
              })}
            >
              <div>
                <p
                  className={styles.columnTitle}
                  style={{
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  Товары
                  <Tooltip
                    title={
                      isProductsLocked
                        ? 'Выбраны подкатегории: скидку получат все их товары. Чтобы выбрать отдельные товары, снимите отмеченные подкатегории, категории с ними помечены числом.'
                        : isSubcategoriesLocked
                          ? 'Выбраны отдельные товары: скидку получат только они. Чтобы выбрать подкатегории, нажмите «Отменить выбор».'
                          : 'Скидку можно дать либо на подкатегории целиком, либо на отдельные товары. Когда выбрано одно, другое недоступно.'
                    }
                  >
                    <QuestionCircleOutlined
                      style={{
                        fontSize: 14,
                        color: 'rgba(0, 0, 0, 0.45)',
                        cursor: 'help'
                      }}
                    />
                  </Tooltip>
                </p>
                <div
                  className={classNames({
                    [styles.scopeDisabled]: isProductsLocked
                  })}
                >
                  <Input.Search
                    allowClear
                    value={productNameSearch}
                    placeholder='Поиск товаров'
                    onChange={(e) => {
                      setProductNameSearch(e.target.value)
                      debouncedGetProductsByName(e.target.value)
                    }}
                    style={{ margin: '8px 0' }}
                  />
                  <Typography.Text strong style={{ display: 'block' }}>
                  Выбрано товаров: {selectedProductsIds.length}
                  </Typography.Text>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <Button
                      size='small'
                      loading={isSelectingAll}
                      disabled={!total}
                      onClick={selectAllFoundProducts}
                    >
                    Все найденные
                    </Button>
                    <Button size='small' onClick={() => setSelectedProductsIds([])}>
                    Отменить выбор
                    </Button>
                  </div>
                </div>
              </div>
              {categories.length > 0 && (
                <>
                  <div
                    className={classNames(styles.categoryColumn, {
                      [styles.scopeDisabled]: isSubcategoriesLocked
                    })}
                  >
                    <p className={styles.columnTitle}>Категории</p>
                    <div className={styles.categoryList}>
                      {categories.map((category) => {
                        const selectedInCategory = category.children.filter(
                          (subcategory) =>
                            selectedSubcategoriesIds.includes(subcategory.value)
                        ).length
                        return (
                          <div
                            key={category.value}
                            className={styles.categoryItem}
                            style={{
                              backgroundColor:
                              selectedCategoryId === category.value
                                ? '#1677FF'
                                : 'transparent',
                              color:
                              selectedCategoryId === category.value
                                ? '#FFFFFF'
                                : 'rgba(0, 0, 0, 0.88)'
                            }}
                            onClick={() => setSelectedCategoryId(category.value)}
                          >
                            {category.title}
                            {selectedInCategory > 0 && ` (${selectedInCategory})`}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  <div
                    className={classNames(styles.subcategoriesColumn, {
                      [styles.scopeDisabled]: isSubcategoriesLocked
                    })}
                  >
                    <div className={styles.subcategoriesColumnHeader}>
                      <Checkbox
                        checked={(() => {
                          const currentCategory = categories.find(
                            (category) => category.value === selectedCategoryId
                          )
                          if (!currentCategory?.children.length) return false
                          return currentCategory.children.every(
                            (subcategory) =>
                              selectedSubcategoriesIds.includes(
                                subcategory.value
                              )
                          )
                        })()}
                        onChange={() => {
                          const currentCategory = categories.find(
                            (category) => category.value === selectedCategoryId
                          )
                          if (!currentCategory) return

                          const currentSubcategoryIds =
                            currentCategory.children.map(
                              (subcategory) => subcategory.value
                            )
                          const allSelected = currentCategory.children.every(
                            (subcategory) =>
                              selectedSubcategoriesIds.includes(
                                subcategory.value
                              )
                          )

                          if (allSelected) {
                            // Убираем все подкатегории текущей категории
                            setSelectedSubcategoriesIds(
                              selectedSubcategoriesIds.filter(
                                (id) => !currentSubcategoryIds.includes(id)
                              )
                            )
                          } else {
                            // Добавляем все подкатегории текущей категории
                            setSelectedSubcategoriesIds([
                              ...selectedSubcategoriesIds.filter(
                                (id) => !currentSubcategoryIds.includes(id)
                              ),
                              ...currentSubcategoryIds
                            ])
                            // выбор подкатегорий исключает выбор конкретных товаров
                            setSelectedProductsIds([])
                          }
                        }}
                      />
                      <p
                        className={classNames(
                          styles.columnTitle,
                          styles.subcategoriesColumnTitle
                        )}
                      >
                        Подкатегории
                      </p>
                    </div>
                    <Input.Search
                      allowClear
                      value={subcategoriesSearch}
                      placeholder='Поиск'
                      size='small'
                      onChange={(e) => setSubcategoriesSearch(e.target.value)}
                      className={styles.search}
                    />
                    <div className={styles.subcategoriesList}>
                      {categories
                        .find(
                          (category) => category.value === selectedCategoryId
                        )
                        ?.children.filter((subcategory) =>
                          subcategory.title
                            .toLocaleLowerCase()
                            .includes(subcategoriesSearch.toLocaleLowerCase())
                        )
                        .map((subcategory) => (
                          <Checkbox
                            key={subcategory.value}
                            className={styles.subcategoryItem}
                            value={subcategory.number}
                            checked={selectedSubcategoriesIds.includes(
                              subcategory.value
                            )}
                            onChange={(e) => {
                              setSelectedSubcategoriesIds(
                                e.target.checked
                                  ? [
                                    ...selectedSubcategoriesIds,
                                    subcategory.value
                                  ]
                                  : selectedSubcategoriesIds.filter(
                                    (id) => id !== subcategory.value
                                  )
                              )
                              // выбор подкатегории исключает выбор конкретных товаров
                              if (e.target.checked) {
                                setSelectedProductsIds([])
                              }
                            }}
                          >
                            {highlightText(
                              subcategory.title,
                              subcategoriesSearch
                            )}
                          </Checkbox>
                        ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <Form.Item
              style={{ marginBottom: 0 }}
              className={classNames({
                [styles.scopeHidden]: isGlobalDiscount
              })}
            >
              <Button
                type='primary'
                htmlType='submit'
                icon={<SaveOutlined />}
                loading={isSubmitting}
                disabled={isSubmitting}
                block
              >
                Сохранить
              </Button>
            </Form.Item>
          </div>
          <div
            className={classNames(styles.productsTableWrapper, {
              [styles.scopeDisabled]: isGlobalDiscount || isProductsLocked
            })}
          >
            <Table
              scroll={isMobile ? undefined : { x: 600 }}
              size={isMobile ? 'small' : undefined}
              tableLayout={isMobile ? 'fixed' : undefined}
              columns={isMobile ? mobileColumns : columns}
              dataSource={products}
              loading={loadingProducts}
              rowKey={(el) => el._id}
              rowSelection={{
                selectedRowKeys: selectedProductsIds,
                onChange: onRowSelection,
                // выбор не слетает при перелистывании страниц
                preserveSelectedRowKeys: true
              }}
              rowClassName='tableRow'
              pagination={{
                current: currentPage,
                onChange: onPageChange,
                pageSize,
                total,
                showSizeChanger: false
              }}
            />
          </div>
        </div>
      </Form>
    </div>
  )
}
